import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { ParameterMetadata, ParameterType, PrimitiveType } from '../../../api/api';
import { FlowAttributeCaptureModel, FlowDefinitionModel } from '../Models/FlowModel';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { GenericProperties } from './GenericProperties';
import { SchemaValueInputDialog } from '../Components/Inputs/SchemaValueInputDialog';
import { useFlowSchemaContext } from '../Components/Contexts/FlowSchemaContext';
import { upsertFlowSchemaValue } from '../Services/FlowSchemaValues';

const ATTRIBUTE_CAPTURES_PARAMETER = 'RuntimeSettings.AttributeCaptures';

const flowParameters: ParameterMetadata[] = [
	{
		name: 'InputSchema',
		displayName: 'Input Schema Entity',
		type: ParameterType.SchemaRef,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Object },
		order: 0,
	},
	{
		name: 'InputSchemaValue',
		displayName: 'Input Schema Value',
		type: ParameterType.SchemaValue,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Object },
		order: 1,
	},
	{
		name: 'OutputSchema',
		displayName: 'Output Schema Entity',
		type: ParameterType.SchemaRef,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Object },
		order: 2,
	},
	{
		name: 'OutputSchemaValue',
		displayName: 'Output Schema Value',
		type: ParameterType.SchemaValue,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Object },
		order: 3,
	},
	{
		name: 'RuntimeSettings.StoreTransitions',
		displayName: 'Store Transitions',
		description: 'Indicates whether to store transition evaluations during flow execution',
		type: ParameterType.Primitive,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Boolean },
		order: 4,
	},
	{
		name: 'RuntimeSettings.TraceEnabled',
		displayName: 'Trace Enabled',
		description: 'Emits detailed flow runtime telemetry at Information level for this flow',
		type: ParameterType.Primitive,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Boolean },
		order: 5,
	},
	{
		name: 'RuntimeSettings.StoreVariables',
		displayName: 'Store Variables',
		description: 'Indicates whether to store variable values at each step of the flow execution for better tracing',
		type: ParameterType.Primitive,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.Boolean },
		order: 6,
	},
	{
		name: 'RuntimeSettings.DeleteAfter',
		displayName: 'Delete After',
		description: 'Indicates whether to delete variables after flow execution',
		type: ParameterType.Primitive,
		isCollection: false,
		categories: [],
		expectedType: { type: PrimitiveType.TimeSpan },
		order: 7,
	},
	{
		name: ATTRIBUTE_CAPTURES_PARAMETER,
		displayName: 'Attribute Captures',
		description: 'Attributes captured from the root variable scope after a flow run finishes',
		type: ParameterType.Primitive,
		isCollection: true,
		categories: [],
		expectedType: { type: PrimitiveType.Object },
		order: 8,
	},
];

type FlowPropertiesProps = {
	flow: FlowDefinitionModel;
	onFlowChanged: (flow: FlowDefinitionModel) => void;
	variableScope?: VariableScope;
	isVariableScopeLoading?: boolean;
	readOnly?: boolean;
};

type AttributeCapturesInputProps = {
	value: FlowAttributeCaptureModel[] | undefined;
	onValueChange: (value: FlowAttributeCaptureModel[]) => void;
	readOnly?: boolean;
};

function AttributeCapturesInput({ value, onValueChange, readOnly }: AttributeCapturesInputProps) {
	const captures = Array.isArray(value) ? value : [];

	const updateCapture = (index: number, patch: Partial<FlowAttributeCaptureModel>) => {
		onValueChange(captures.map((capture, currentIndex) => (currentIndex === index ? { ...capture, ...patch } : capture)));
	};

	const removeCapture = (index: number) => {
		onValueChange(captures.filter((_, currentIndex) => currentIndex !== index));
	};

	return (
		<Stack spacing={1}>
			{captures.map((capture, index) => (
				<Box key={index} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 32px', gap: 1, alignItems: 'end' }}>
					<TextField
						variant="standard"
						label="Key"
						value={capture.Key ?? ''}
						disabled={readOnly}
						onChange={(event) => updateCapture(index, { Key: event.target.value })}
						fullWidth
					/>
					<TextField
						variant="standard"
						label="Value"
						placeholder="customer.id"
						value={capture.Value ?? ''}
						disabled={readOnly}
						onChange={(event) => updateCapture(index, { Value: event.target.value })}
						fullWidth
					/>
					<Tooltip title="Remove attribute">
						<IconButton size="small" aria-label="Remove attribute" onClick={() => removeCapture(index)} disabled={readOnly}>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			))}
			<Button
				size="small"
				startIcon={<AddCircleIcon fontSize="small" />}
				onClick={() => onValueChange([...captures, { Key: '', Value: '' }])}
				disabled={readOnly}
				sx={{ alignSelf: 'flex-start' }}
			>
				Add
			</Button>
		</Stack>
	);
}

export function FlowProperties({ flow, onFlowChanged, variableScope, isVariableScopeLoading, readOnly }: FlowPropertiesProps) {
	const [editingSchemaId, setEditingSchemaId] = useState<string>();
	const usageCounts = useFlowSchemaContext()?.usageCounts;
	const editingSchema = flow.SchemaValues?.find((schema) => schema.Id === editingSchemaId);
	return (
		<Stack spacing={2}>
		<GenericProperties
			name={flow.Name}
			parameters={flowParameters}
			model={flow}
			onModelChanged={onFlowChanged}
			variableScope={variableScope}
			isVariableScopeLoading={isVariableScopeLoading}
			readOnly={readOnly}
		>
			{(parameterMetadata, value, onValueChange) =>
				parameterMetadata.name === ATTRIBUTE_CAPTURES_PARAMETER ? <AttributeCapturesInput value={value} onValueChange={onValueChange} readOnly={readOnly} /> : null
			}
		</GenericProperties>
		<Box sx={{ px: 2, pb: 2 }}>
			<Typography variant="subtitle2">Shared schemas</Typography>
			{!flow.SchemaValues?.length && <Typography variant="caption" color="text.secondary">Save an inline schema as shared to reuse it here.</Typography>}
			{flow.SchemaValues?.map((schema) => {
				const uses = usageCounts?.get(schema.Id) ?? 0;
				return (
					<Stack key={schema.Id} direction="row" alignItems="center" spacing={0.5}>
						<Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>{schema.Name || schema.Value.title || schema.Id} ({uses} uses)</Typography>
						<IconButton size="small" aria-label={`Edit ${schema.Name || 'shared schema'}`} disabled={readOnly} onClick={() => setEditingSchemaId(schema.Id)}><EditIcon fontSize="small" /></IconButton>
						<Tooltip title={uses ? 'Remove references before deleting' : 'Delete shared schema'}>
							<span><IconButton size="small" aria-label={`Delete ${schema.Name || 'shared schema'}`} disabled={readOnly || uses > 0}
								onClick={() => onFlowChanged({ ...flow, SchemaValues: flow.SchemaValues?.filter((item) => item.Id !== schema.Id) })}><DeleteIcon fontSize="small" /></IconButton></span>
						</Tooltip>
					</Stack>
				);
			})}
		</Box>
		{editingSchema && <SchemaValueInputDialog open setOpen={(open) => { if (!open) setEditingSchemaId(undefined); }} schemaValue={editingSchema}
			onSchemaValueChange={(updated) => {
				if (updated) onFlowChanged(upsertFlowSchemaValue(flow, { ...updated, Id: editingSchema.Id }));
			}} />}
		</Stack>
	);
}
